import { Box } from "@dropbox/dig-foundations";
import { BasicUserProfileCard } from "@mirage/mosaics/BaseProfilePage/BasicUserProfileCard";
import { UserProfileHoverCardPortal } from "@mirage/mosaics/BaseProfilePage/UserProfileHoverCardPortal";
import { getUserProfilePageHref } from "@mirage/mosaics/UserProfilePage/utils";
import { usePersonFromOrgChartByEmail } from "@mirage/service-people/hooks";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

const HOVER_OFFSET = 8;

export const UserProfileHoverCard = ({
  personEmail,
  children,
  className,
  visualBottomSpacing = 0,
}: {
  personEmail?: string;
  children: React.ReactNode;
  className?: string;
  visualBottomSpacing?: number;
}) => {
  const person = usePersonFromOrgChartByEmail(personEmail);

  const timeoutRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [shouldShowBelow, setShouldShowBelow] = useState(true);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (isHovered && wrapperRef.current) {
      const triggerRect = wrapperRef.current.getBoundingClientRect();

      // If card is too tall, show hover card below the trigger
      const spaceAboveTrigger = triggerRect.top;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const shouldShowBelow = spaceBelow >= spaceAboveTrigger;
      setShouldShowBelow(shouldShowBelow);

      // Compute position for hover card
      const top = shouldShowBelow
        ? triggerRect.bottom + window.scrollY + HOVER_OFFSET
        : triggerRect.top + window.scrollY - HOVER_OFFSET;
      const left = triggerRect.left + window.scrollX;

      setPosition({ top, left });
    }
  }, [isHovered]);

  const navigate = useNavigate();
  const onViewProfile = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (person?.displayHandle) {
        const href = getUserProfilePageHref(person.displayHandle);
        if (href) {
          navigate(href);
        }
      }
    },
    [person?.displayHandle, navigate],
  );

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    // Set delay to avoid flickering when user tries to click on hover card
    timeoutRef.current = window.setTimeout(() => {
      setIsHovered(false);
    }, 150);
  };

  useEffect(() => {
    // Clear timeout when component unmounts
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!person) return <>{children}</>;

  return (
    <Box
      as="div"
      ref={wrapperRef}
      position="relative"
      display="inline-block"
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isHovered && position && (
        <UserProfileHoverCardPortal>
          <Box
            position="absolute"
            style={{
              top: position.top,
              left: position.left,
              transform: shouldShowBelow
                ? `translateY(${visualBottomSpacing}px)`
                : "translateY(-100%)",
              zIndex: 1000,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <BasicUserProfileCard
              person={person}
              onViewProfile={onViewProfile}
            />
          </Box>
        </UserProfileHoverCardPortal>
      )}
    </Box>
  );
};
